import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useState } from 'react';
import {
  selectedGeocodeLocation,
  viewAllTeamId,
} from '@/common/atoms/community';
import { isTestTeamName } from '@/common/utils/isTestTeamName';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import { identityApi } from '$api-clients/clients';
import { getViewBox } from '../../components/MapRenderer/helpers';
import {
  type InitialLocation,
  LocationType,
  type TeamSearchFields,
  type TeamSearchResult,
} from '../../types';
import { toTeamSearchFields } from '../../utils/team';
import {
  buildSortParams,
  buildTeamLocationsQuery,
  type SortModel,
} from '../../utils/teamLocationsQuery';
import { useGetTeamOrganization } from '../useGetTeamOrganization';
import { groupPartnersByLocation } from './helpers';

/** Collapses a team's alias when it duplicates the legal name and falls back to
 * the horizontal logo, matching how partner cards expect the data. */
const toPartnerResults = (teams: TeamSearchFields[]): TeamSearchFields[] =>
  teams.map((team) => ({
    ...team,
    alias: team.legalName === team.alias ? null : team.alias,
    logoUri: team.logoUri ?? team.logoHorizontalUri,
  }));

export const useGetPartners = () => {
  const trackEvent = useTrackCommunityAnalytics();
  const [partnerGroups, setPartnerGroups] = useState<
    Record<string, TeamSearchResult[]>
  >({});
  const [loading, setLoading] = useState(true);
  const currentLocation = useAtomValue(selectedGeocodeLocation);
  const viewAllTeam = useAtomValue(viewAllTeamId);
  const { teams: orgTeams } = useGetTeamOrganization(viewAllTeam);

  const getSortValue = useCallback(
    (initialCenter: InitialLocation): SortModel[] => {
      const isLocaleSearch =
        currentLocation &&
        !currentLocation.id.startsWith('team:') &&
        !Object.values(LocationType).some(
          (type) => type === currentLocation.id,
        );

      if (isLocaleSearch) {
        return [
          {
            field: 'distance',
            sort: 'asc',
            center: {
              lat: currentLocation.latitude,
              lng: currentLocation.longitude,
            },
          },
        ];
      }

      return [
        {
          field: 'distance',
          sort: 'asc',
          center: { lat: initialCenter.lat, lng: initialCenter.lng },
        },
      ];
    },
    [currentLocation],
  );

  const getTeamsByViewBox = useCallback(
    async (map: google.maps.Map | null, initialCenter: InitialLocation) => {
      const bounds = map?.getBounds();
      if (!bounds) return [];

      const viewBox = getViewBox(bounds);
      if (!viewBox) return [];

      const sort = getSortValue(initialCenter);
      const zoomLevel = map?.getZoom();
      const { data, error } = await identityApi.GET('/teams/locations', {
        params: {
          query: {
            q: buildTeamLocationsQuery({
              viewBox,
              status: 'ACTIVE',
            }),
            ...buildSortParams(sort),
            limit: zoomLevel && zoomLevel > 7 ? 550 : 1000,
          },
        },
      });

      if (error !== undefined) {
        datadogRum.addError('Unable to load partner map teams.', {
          error,
          viewBox,
        });
        return [];
      }

      return (data?.data ?? [])
        .filter(
          (team) =>
            process.env.NEXT_PUBLIC_APP_ENV !== 'prod' ||
            !isTestTeamName(team.legalName),
        )
        .map(toTeamSearchFields);
    },
    [getSortValue],
  );

  // View-all mode: the groups come from the cached org walk and don't depend on
  // the viewport, so derive them here instead of refetching on every bounds
  // change (the viewport query path is handled imperatively by getTeams).
  useEffect(() => {
    if (!viewAllTeam) {
      return;
    }
    if (orgTeams === undefined) {
      setLoading(true);
      return;
    }
    setPartnerGroups(groupPartnersByLocation(toPartnerResults(orgTeams)));
    setLoading(false);
  }, [viewAllTeam, orgTeams]);

  const fetchTeamsCallback = useAtomCallback((get) => {
    return async (
      map: google.maps.Map | null,
      initialCenter: InitialLocation,
    ) => {
      // View-all groups are derived declaratively above; here we only fetch the
      // teams in the current viewport.
      if (!get(viewAllTeamId)) {
        setLoading(true);
        const rawTeams = await getTeamsByViewBox(map, initialCenter);
        setPartnerGroups(groupPartnersByLocation(toPartnerResults(rawTeams)));
        setLoading(false);
      }

      const bounds = map?.getBounds();
      if (bounds) {
        trackEvent(TrackCommunityEvent.PartnerMapDataFetch, {
          geoValue: [bounds.getCenter().lat(), bounds.getCenter().lng()],
        });
      }
    };
  });

  const getTeams = useCallback(
    async (map: google.maps.Map | null, initialCenter: InitialLocation) => {
      const callback = fetchTeamsCallback();
      return callback(map, initialCenter);
    },
    [fetchTeamsCallback],
  );

  return {
    getTeams,
    partnerGroups,
    loading,
  };
};
